import { useParams, useSearchParams } from "react-router";
import { DocumentPreview } from "~/molecules/DocumentPreview";
import { useDocument } from "~/api/v2/useDocument";
import { CenterSpinner } from "~/atoms/spinner";
import { useTranslation } from "react-i18next";
import NoResult from "~/molecules/NoResult";
import { useState, useEffect } from "react";
import { VersionSelect } from "~/molecules/VersionSelect";
import { DocumentBreadcrumbs } from "~/molecules/DocumentBreadcrumbs";
import { useUserContext } from "~/hooks/useUserContext";
import { PlatformRole } from "~/config/types";
import { ArchivedWarning } from "~/molecules/ArchivedWarning";
import { DocumentConfetti } from "~/molecules/DocumentConfetti";
import { Link } from "react-router";
import { Button, buttonVariants } from "~/atoms/button";
import { History, Ban, Copy, MoreVertical, Archive } from "lucide-react";
import { FetchError } from "~/api/fetcher";
import { toast } from "react-toastify";
import { DocumentPanelTabs } from "~/molecules/DocumentPanelTabs";
import { EditableDocumentTitle } from "~/molecules/EditableDocumentTitle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
} from "~/atoms/dropdown-menu";
import { archiveDocument } from "~/api/v2/requests";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "~/atoms/alert-dialog";
import {
  canArchiveDocument,
  canEditDocumentContent,
  isDocumentOrVersionArchived,
} from "~/helpers/documentPermissions";

const ArchiveDocumentDropdownMenuItem = ({
  documentId,
  onArchive,
}: {
  documentId: number;
  onArchive: () => void;
}) => {
  const { t } = useTranslation();

  const handleArchiveDocument = async () => {
    try {
      await archiveDocument(documentId);
      toast.success(t(($) => $.toasts.success.projectArchived));
      void onArchive();
    } catch {
      toast.error(t(($) => $.toasts.error.projectNotArchived));
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <DropdownMenuItem
          className="text-destructive"
          onSelect={(e) => e.preventDefault()}
        >
          <Archive className="size-4" />
          {t(($) => $.commons.buttons.archiveProject)}
        </DropdownMenuItem>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t(($) => $.modal.archiveProject.title)}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t(($) => $.modal.archiveProject.confirmDescription)}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {t(($) => $.commons.buttons.cancel)}
          </AlertDialogCancel>
          <AlertDialogAction
            className={buttonVariants({ variant: "destructive" })}
            onClick={handleArchiveDocument}
          >
            {t(($) => $.commons.buttons.archiveProject)}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const DocumentPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user } = useUserContext();
  const [searchParams] = useSearchParams();

  const [showConfetti, setShowConfetti] = useState(false);

  const versionIdFromUrl = searchParams.get("versionId");
  const versionId = versionIdFromUrl ? parseInt(versionIdFromUrl) : undefined;

  const { data: doc, isLoading, error, mutate } = useDocument(id);

  const selectedVersion = versionId
    ? doc?.versions.find((version) => version.id === versionId)
    : doc?.versions[0];

  useEffect(() => {
    if (selectedVersion?.title) {
      document.title = `${selectedVersion.title} | North House`;
    }
  }, [selectedVersion?.title]);

  useEffect(() => {
    if (doc?.id == null || selectedVersion?.id == null) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [doc?.id, selectedVersion?.id]);

  if (isLoading) {
    return <CenterSpinner withText />;
  }

  if (error && error instanceof FetchError && error.status === 403) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-12">
        <Ban className="text-muted-foreground h-10 w-10" />
        <p className="text-muted-foreground text-center font-medium">
          {t(($) => $.commons.document.youDontHaveAccess.title)}
        </p>
        <p className="text-muted-foreground text-center text-sm">
          {t(($) => $.commons.document.youDontHaveAccess.subtitle)}
        </p>
      </div>
    );
  }

  if (!doc || !selectedVersion) {
    return <NoResult message={t(($) => $.commons.noItems)} />;
  }

  const showArchivedWarning = isDocumentOrVersionArchived(
    selectedVersion,
    doc.archivedAt,
  );
  const canEditDocument = canEditDocumentContent(
    selectedVersion,
    doc.archivedAt,
  );

  const triggerConfetti = () => {
    setShowConfetti(true);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/projects/${doc.projectId}`,
      );
      toast.success(t(($) => $.toasts.success.linkCopied));
    } catch {
      toast.error(t(($) => $.toasts.error.copyFailed));
    }
  };

  return (
    <div className="grid-rows-document-overview container grid py-6">
      <DocumentConfetti
        show={showConfetti}
        onComplete={() => setShowConfetti(false)}
      />

      <div className="flex justify-between">
        <DocumentBreadcrumbs
          documentId={doc.id}
          versionId={selectedVersion.id}
          title={selectedVersion.title}
        />
        {showArchivedWarning && (
          <ArchivedWarning id={doc.id} archivedAt={doc.archivedAt} />
        )}
      </div>

      <div className="flex flex-wrap justify-between gap-4 pt-2">
        <div className="flex flex-1 items-center gap-2">
          {canEditDocument ? (
            <EditableDocumentTitle
              document={doc}
              selectedVersion={selectedVersion}
              onUpdate={mutate}
            />
          ) : (
            <h2 className="text-2xl font-semibold text-wrap wrap-break-word">
              {selectedVersion.title}
            </h2>
          )}
        </div>

        <div className="flex items-center gap-2">
          {user.role !== PlatformRole.reader && (
            <VersionSelect
              selectedVersionId={selectedVersion.id}
              documentVersions={doc.versions}
              documentId={doc.id}
              onVersionChange={mutate}
            />
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleCopyLink}>
                <Copy className="size-4" />
                {t(($) => $.commons.workflow.copyLinkToPublishedDocument)}
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <Link to={`/documents/${doc.id}/audit-trail`}>
                  <History className="size-4" />
                  {t(($) => $.commons.documentHistory.header)}
                </Link>
              </DropdownMenuItem>
              {canArchiveDocument(selectedVersion, doc.archivedAt) && (
                <ArchiveDocumentDropdownMenuItem
                  documentId={doc.id}
                  onArchive={mutate}
                />
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 pt-2">
        <div className="col-span-3 xl:col-span-2">
          <DocumentPreview
            key={selectedVersion.fileUpdatedAt}
            onUpdate={mutate}
            version={selectedVersion}
          />
        </div>
        <div className="col-span-3 xl:col-span-1">
          <DocumentPanelTabs
            user={user}
            selectedVersion={selectedVersion}
            document={doc}
            onChange={mutate}
            onConfetti={triggerConfetti}
          />
        </div>
      </div>
    </div>
  );
};
